import { TaMenu } from '@components/layout/dashboardLayout';
import { RoleSwitchLoader } from '@components/RoleSwitchLoader';
import { LanguageSwitcher } from '@components/shared/LanguageSwitcher';
import { SpecialistCard } from '@features/dashboard/specialistDashboard/specialistSwitchLayout/SpecialistCard';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useRoleSwitch } from '@hooks/useRoleSwitch';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
// import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsIcon from '@mui/icons-material/Settings';
import {
	Backdrop,
	//  Badge,
	CircularProgress,
} from '@mui/material';
import Button from '@mui/material/Button';
// import IconButton from '@mui/material/IconButton';
import { RoleEnum } from '@utils/util.types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogout } from 'services/auth';
import { useUsersStore } from 'services/users';

const backdropStyle = {
	color: '#fff',
	zIndex: (theme: any) => theme.zIndex.drawer + 1,
	display: 'flex',
	flexDirection: 'column',
	gap: 2,
};

export const SpecialistSwitch = () => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { switchRole, isLoading } = useRoleSwitch();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);
	const { logout } = useLogout();
	const { user, setUser, loading } = useUsersStore();

	const handleLogout = async () => {
		handleClose();
		setIsLoggingOut(true);
		await logout();
		setUser(null);
		setIsLoggingOut(false);
		navigateWithLanguage('/login');
	};
	const [isLoggingOut, setIsLoggingOut] = useState(false);

	const specialistMenuItems = [
		{
			icon: <AccountCircleIcon className="mr-[10px]" />,
			text: t('myProfile'),
			disabled: !user?.is_active,
			link: '/specialist/profile',
			onClick: () => {
				handleClose();
				navigateWithLanguage('/specialist/profile');
			},
		},
		{
			icon: <SettingsIcon className="mr-[10px]" />,
			text: t('accountSettings'),
			link: '/specialist/settings',
			disabled: !user?.is_active,
			onClick: () => {
				navigateWithLanguage('/specialist/settings');
				handleClose();
			},
		},
		{
			icon: <LogoutOutlinedIcon className="mr-[10px]" />,
			text: t('logout'),
			disabled: false,
			onClick: handleLogout,
		},
	];

	const farmerMenuItems = [
		{
			icon: <AccountCircleIcon className="mr-[10px]" />,
			text: t('myProfile'),
			link: '/farmer/profile',
			onClick: () => {
				handleClose();
				navigateWithLanguage('/farmer/profile');
			},
		},
		{
			icon: <SettingsIcon className="mr-[10px]" />,
			text: t('accountSettings'),
			link: '/farmer/settings',
			onClick: () => {
				navigateWithLanguage('/farmer/settings');
				handleClose();
			},
		},
		{
			icon: <LogoutOutlinedIcon className="mr-[10px]" />,
			text: t('logout'),
			disabled: isLoggingOut,
			onClick: handleLogout,
		},
	];

	const menuItems = user?.role === RoleEnum.Farmer ? farmerMenuItems : specialistMenuItems;

	if (loading) {
		return (
			<div className="flex h-screen flex-col items-center justify-center bg-main-bg">
				<CircularProgress color="primary" />
			</div>
		);
	}

	const handleClickSwitchRole = () => {
		switchRole('farmer');
	};

	return (
		<div className="relative">
			<div
				className={`absolute top-[20px] right-[20px] flex justify-end items-center max-sm:top-[10px]  max-sm:right-[10px]`}
			>
				<div className="flex justify-center items-center gap-[12px]">
					<div className="dashboard-user max-xsm:hidden">
						{t('hi')} {user?.first_name}
					</div>
					<div className="flex items-center flex-col relative">
						<Button
							type="submit"
							variant="outlined"
							sx={{
								padding: '9px 18px',
								fontWeight: '700',
								fontSize: '12px',
							}}
							onClick={handleClick}
						>
							<AccountCircleOutlinedIcon className="mr-[10px]" />
							{user?.is_company ? t('company') : t('specialists')}
						</Button>
						{!user?.is_active && (
							<span className="text-orange-500 text-xs absolute bottom-[-24px] text-nowrap">
								{t('userProfilePending')}
							</span>
						)}
					</div>
					<LanguageSwitcher />
				</div>
			</div>
			<div className="flex h-screen flex-col items-center justify-center bg-main-bg">
				<h1 className="text-dark text-center roboto-bold text-[35px]  leading-[49px] mb-[37px]">
					{t('welcome')}
				</h1>
				<div className="flex flex-col gap-[20px] pb-5">
					<SpecialistCard
						cardClick={() => navigateWithLanguage(user?.is_active ? '/specialist/dashboard' : '')}
						title={`${t('specialistsAccount')}`}
						label={user?.is_active ? `${t('activated')}` : `${t('deactivated')}...`}
						isActive={user?.is_active}
					/>
					<SpecialistCard
						cardClick={handleClickSwitchRole}
						title={`${t('findSpecialists')}`}
						description={`${t('otherSpecialists')}`}
						isActive={true}
					/>
				</div>
			</div>
			<TaMenu anchorEl={anchorEl} open={open} onClose={handleClose} menuItems={menuItems} />
			<RoleSwitchLoader isLoading={isLoading} />
			<Backdrop sx={backdropStyle} open={isLoggingOut}>
				<CircularProgress color="primary" />
			</Backdrop>
		</div>
	);
};
